/**
 * Biometric key-wrap helpers.
 *
 * These functions wrap and unwrap the seed-encryption key (the same
 * 32-byte Argon2id-derived key that the cipher module uses) under a
 * second key derived from a WebAuthn PRF extension output.
 *
 * Algorithm: HKDF-SHA-256 over the PRF secret to derive a 32-byte
 * AES-256 key, then AES-256-GCM(nonce, ciphertext+tag).
 *
 * We don't store the PRF secret anywhere — it lives only on the device's
 * Secure Enclave / TPM and is returned to JS only after a successful
 * Face ID / Touch ID / Windows Hello prompt. Stealing the IndexedDB
 * blob without the device gets you nothing.
 *
 * The PIN-based seed encryption (cipher.ts) is unchanged. Biometric
 * unlock is an *additional* recovery path that wraps the same derived
 * key, so disabling biometrics doesn't break PIN unlock and vice versa.
 */
const NONCE_LENGTH = 12; // AES-GCM nonce
const KEY_LENGTH = 32; // AES-256
async function deriveAesKeyFromPrf(prfSecret) {
    // Import the raw PRF output as HKDF input keying material.
    const ikm = await crypto.subtle.importKey('raw', prfSecret, 'HKDF', false, ['deriveKey']);
    // HKDF with empty salt and a domain-separation info string. Output
    // is a non-extractable AES-256-GCM key — even after the call, the
    // raw bytes never re-enter JS land.
    return crypto.subtle.deriveKey({
        name: 'HKDF',
        hash: 'SHA-256',
        salt: new Uint8Array(0),
        info: new TextEncoder().encode('gonka-wallet-biometric-wrap-v1'),
    }, ikm, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}
/**
 * Wrap a 32-byte derived key under a PRF secret. Output is
 * `nonce || ciphertext+tag` as a Uint8Array (12 + 32 + 16 = 60 bytes).
 */
export async function wrapKeyWithPrf(derivedKey, prfSecret) {
    if (derivedKey.length !== KEY_LENGTH) {
        throw new Error(`derivedKey must be ${KEY_LENGTH} bytes`);
    }
    if (prfSecret.length < KEY_LENGTH) {
        throw new Error(`prfSecret must be at least ${KEY_LENGTH} bytes`);
    }
    const aesKey = await deriveAesKeyFromPrf(prfSecret);
    const nonce = crypto.getRandomValues(new Uint8Array(NONCE_LENGTH));
    const ciphertextBuf = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce }, aesKey, derivedKey);
    const ciphertext = new Uint8Array(ciphertextBuf);
    const out = new Uint8Array(NONCE_LENGTH + ciphertext.length);
    out.set(nonce, 0);
    out.set(ciphertext, NONCE_LENGTH);
    return out;
}
/**
 * Unwrap a `nonce || ciphertext+tag` blob under a PRF secret. Throws on
 * any tampering or PRF mismatch (AES-GCM auth tag failure).
 */
export async function unwrapKeyWithPrf(wrapped, prfSecret) {
    if (wrapped.length < NONCE_LENGTH + 16) {
        throw new Error('wrapped key too short');
    }
    if (prfSecret.length < KEY_LENGTH) {
        throw new Error(`prfSecret must be at least ${KEY_LENGTH} bytes`);
    }
    const aesKey = await deriveAesKeyFromPrf(prfSecret);
    const nonce = wrapped.subarray(0, NONCE_LENGTH);
    const ciphertext = wrapped.subarray(NONCE_LENGTH);
    const plainBuf = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: nonce }, aesKey, ciphertext);
    const plain = new Uint8Array(plainBuf);
    if (plain.length !== KEY_LENGTH) {
        throw new Error(`unwrapped key has wrong length: ${plain.length}`);
    }
    return plain;
}
/**
 * Generate a fresh 32-byte salt to feed to the PRF extension. We bind
 * each wallet to its own salt so two wallets on the same device get
 * different PRF outputs from the same passkey.
 */
export function generatePrfSalt() {
    return crypto.getRandomValues(new Uint8Array(32));
}
//# sourceMappingURL=biometric.js.map